// src/lib/measurement/problemFactories/mass.ts

import { Rng } from '../../rng';
import { formatWithUnit, makeNearbyNumberOptions, makeUnitOptions } from '../distractors';
import type { MeasurementProblem } from './length';

type Difficulty = 1 | 2 | 3;

const BEST_UNIT_ITEMS: Array<{ item: string; unit: 'g' | 'kg' }> = [
  { item: 'apple', unit: 'g' },
  { item: 'book', unit: 'g' },
  { item: 'pencil', unit: 'g' },
  { item: 'bag of flour', unit: 'kg' },
  { item: 'dog', unit: 'kg' },
  { item: 'elephant', unit: 'kg' },
  { item: 'watermelon', unit: 'kg' },
];

const ESTIMATE_ITEMS: Array<{ item: string; unit: 'g' | 'kg'; correct: number; distractorSteps: number[] }> = [
  { item: 'apple', unit: 'g', correct: 200, distractorSteps: [100, 150, 250, 300, 500] },
  { item: 'banana', unit: 'g', correct: 150, distractorSteps: [80, 100, 200, 250, 300] },
  { item: 'textbook', unit: 'g', correct: 900, distractorSteps: [500, 700, 1100, 1300, 1500] },
  { item: 'bag of rice', unit: 'kg', correct: 2, distractorSteps: [1, 3, 4, 5, 6] },
  { item: 'baby', unit: 'kg', correct: 4, distractorSteps: [2, 3, 5, 6, 7] },
  { item: 'cat', unit: 'kg', correct: 5, distractorSteps: [2, 3, 4, 6, 8] },
];

export function makeMassBestUnit(rng: Rng, id: string, difficulty: Difficulty): MeasurementProblem {
  void difficulty;
  const { item, unit } = rng.pick(BEST_UNIT_ITEMS);

  return {
    id,
    kind: 'mcq',
    type: 'mass_best_unit',
    prompt: `What is the best unit to measure the mass of a ${item}?`,
    choices: makeUnitOptions('g', 'kg'),
    meta: {
      topicArea: 'mass',
      difficulty,
      expected: unit,
      templateKey: 'mass_best_unit',
      data: { item },
    },
  };
}

export function makeMassEstimateCommon(rng: Rng, id: string, difficulty: Difficulty): MeasurementProblem {
  void difficulty;
  const picked = rng.pick(ESTIMATE_ITEMS);
  const correct = formatWithUnit(picked.correct, picked.unit);

  const out = new Set<string>();
  out.add(correct);

  let guard = 0;
  while (out.size < 4 && guard < 200) {
    guard++;
    const d = rng.pick(picked.distractorSteps);
    if (d === picked.correct) continue;
    if (d <= 0) continue;
    out.add(formatWithUnit(d, picked.unit));
  }

  return {
    id,
    kind: 'mcq',
    type: 'mass_estimate_common',
    prompt: `About how much does a ${picked.item} weigh?`,
    choices: rng.shuffle(Array.from(out)),
    meta: {
      topicArea: 'mass',
      difficulty,
      expected: correct,
      templateKey: 'mass_estimate_common',
      data: { item: picked.item, unit: picked.unit },
    },
  };
}

export function makeMassBalanceScale(rng: Rng, id: string, difficulty: Difficulty): MeasurementProblem {
  void difficulty;
  const itemLabel = rng.pick(['bags', 'boxes', 'water bottles', 'packs'] as const);

  const totalKg = rng.pick([1, 2, 3, 4] as const);
  const eachKg = totalKg / 2;

  const expected = formatWithUnit(eachKg, 'kg');
  const choices = makeNearbyNumberOptions(rng, eachKg, 'kg', 0.5, 4);

  return {
    id,
    kind: 'mcq',
    type: 'mass_balance_scale',
    prompt: `A scale shows ${totalKg} kg for two identical ${itemLabel}. What is the mass of one ${itemLabel.slice(0, -1)}?`,
    choices,
    meta: {
      topicArea: 'mass',
      difficulty,
      expected,
      unitHint: 'kg',
      templateKey: 'mass_balance_scale',
      figure: { kind: 'balance', maxKg: 4, totalKg, itemLabel },
      data: { totalKg, eachKg },
    },
  };
}

export function makeMassConversionGKg(rng: Rng, id: string, difficulty: Difficulty): MeasurementProblem {
  void difficulty;
  const dir = rng.chance(0.5) ? 'g_to_kg' : 'kg_to_g';

  if (dir === 'g_to_kg') {
    const g = rng.pick([1000, 2000, 3000] as const);
    return {
      id,
      kind: 'input',
      type: 'mass_conversion_g_kg',
      prompt: `Convert ${g} g to kg.`,
      meta: {
        topicArea: 'mass',
        difficulty,
        expected: g / 1000,
        unitHint: 'kg',
        templateKey: 'mass_conversion_g_kg:g_to_kg',
        data: { dir, g },
      },
    };
  }

  const kg = rng.pick([1, 2, 3] as const);
  return {
    id,
    kind: 'input',
    type: 'mass_conversion_g_kg',
    prompt: `Convert ${kg} kg to g.`,
    meta: {
      topicArea: 'mass',
      difficulty,
      expected: kg * 1000,
      unitHint: 'g',
      templateKey: 'mass_conversion_g_kg:kg_to_g',
      data: { dir, kg },
    },
  };
}
